import { useCallback } from 'react';
import { useTermsAcceptance } from '../../providers';
import { useLogOperationCall } from '../useLogOperationCall/useLogOperationCall';

interface AcceptResult {
  success: boolean;
  error?: unknown;
  skipped?: boolean;
  eulaAccepted?: boolean;
  humioAccepted?: boolean;
  sessionToken?: string;
}

interface AcceptAllPendingIfSSOOptions {
  idToken?: string;
  sessionTokenOverride?: string;
  source?: string;
  onToken?: (token: string) => void;
}

export const useAcceptPendingTerms = () => {
  const logOperationCall = useLogOperationCall();
  const { acceptTermsIfNeeded, state: termsState } = useTermsAcceptance();

  const acceptAllPendingIfSSO = useCallback(
    async (opts?: AcceptAllPendingIfSSOOptions): Promise<AcceptResult> => {
      try {
        const result = await acceptTermsIfNeeded({
          sessionToken: opts?.sessionTokenOverride || '',
        });

        // Handle onToken callback if provided
        if (result.success === true && result.sessionToken && opts?.onToken) {
          opts.onToken(result.sessionToken);
        }

        // Log operation if EULA was accepted
        if (result.success === true && result.eulaAccepted === true) {
          try {
            await logOperationCall('PRIVACY_POLICY_ACKNOWLEDGED', 'USER');
          } catch (e) {
            console.error('[acceptAllPendingIfSSO] logOperationCall error:', e);
          }
        }

        return {
          success: result.success,
          eulaAccepted:
            result.success === true ? result.eulaAccepted || false : false,
          humioAccepted:
            result.success === true ? result.humioAccepted || false : false,
          sessionToken:
            result.success === true ? result.sessionToken : undefined,
          error: result.success === false ? result.error : undefined,
        };
      } catch (error) {
        console.error('[acceptAllPendingIfSSO] Unexpected error:', error);
        return { success: false, error };
      }
    },
    [acceptTermsIfNeeded, logOperationCall],
  );

  // Legacy methods for backward compatibility
  const acceptEulaNow = useCallback(async (): Promise<AcceptResult> => {
    const result = await acceptTermsIfNeeded({ sessionToken: '' });
    return {
      success: result.success,
      eulaAccepted: result.success,
      humioAccepted: false,
    };
  }, [acceptTermsIfNeeded]);

  const acceptHumioNow = useCallback(
    async (opts?: AcceptAllPendingIfSSOOptions): Promise<AcceptResult> => {
      return acceptTermsIfNeeded({
        acceptHumioOnly: true,
        ...opts,
      });
    },
    [acceptTermsIfNeeded],
  );

  return {
    acceptEulaNow,
    acceptHumioNow,
    acceptAllPendingIfSSO,
    isProcessingTerms: termsState.isProcessing,
    termsAccepted: termsState.eulaAccepted && termsState.humioAccepted,
  };
};
